import React, {
	type ReactNode,
	useState,
	useMemo,
	useEffect,
	useCallback,
	type PropsWithChildren,
} from 'react';
import OriginTracer from '@atlassiansox/origin-tracing';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import Image from '@atlaskit/image';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { getAdminBaseUrl } from '@atlassian/jira-external-urls';
import { useFlagsService } from '@atlassian/jira-flags';
import {
	getHamsProductKeyForProject,
	getProductOfferingKeyForProject,
} from '@atlassian/jira-growth-utils';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	FireScreenAnalytics,
	ContextualAnalyticsData,
	MODAL,
	type AnalyticsAttributes,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	uiAiUpsellInEditorFreeModal_GetStartPremiumTrialUrlQuery,
	uiAiUpsellInEditorFreeModal_GetStartPremiumTrialUrlQuery$data,
} from '@atlassian/jira-relay/src/__generated__/uiAiUpsellInEditorFreeModal_GetStartPremiumTrialUrlQuery.graphql';
import { Link } from '@atlassian/jira-router';
import { PREMIUM_EDITION } from '@atlassian/jira-shared-types';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import UpsellModal, { type UpsellModalProps } from '@atlassian/upsell-modal';
import background from './assets/background.svg';
import brainstorm from './assets/brainstorm.gif';
import fixSpellingAndGrammar from './assets/spelling.gif';
import changeYourTone from './assets/tone.gif';
import improveYourWriting from './assets/writing.gif';
import { LineItem } from './line-item';
import { Logo } from './logo';
import { messages } from './messages';
import type {
	AiUpsellInEditorFreeModalProps,
	ModalContentProps,
	OnCarouselItemChange,
} from './types';

const FALLBACK_URL = `${getAdminBaseUrl()}/billing`;

const GifBackground = (props: PropsWithChildren) => {
	const { children } = props;
	return (
		<Box
			xcss={backgroundStyles}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				backgroundImage: `url("${background}")`,
			}}
		>
			{children}
		</Box>
	);
};

export function AiUpsellInEditorFreeModal(props: AiUpsellInEditorFreeModalProps) {
	return (
		<ContextualAnalyticsData
			sourceName="aiUpsellInEditorFree"
			sourceType={MODAL}
			attributes={{ projectType: props.projectType }}
		>
			<AiUpsellInEditorFreeModalInner {...props} />
		</ContextualAnalyticsData>
	);
}

function AiUpsellInEditorFreeModalInner(props: AiUpsellInEditorFreeModalProps) {
	const { testId, projectType, onModalClose } = props;

	// -----  HOOKS -----
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const environment = useRelayEnvironment();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlagsService();

	// -----  STATE -----
	const [startTrialUrl, setStartTrialUrl] = useState<string>(FALLBACK_URL);
	// For the Carousel
	const [hasSelected, setHasSelected] = useState<boolean>(false);
	const [selectedIndex, setSelectedIndex] = useState<number>(0);

	// -----  LISTENERS -----
	// Carousel Listeners
	const onCarouselItemChange: OnCarouselItemChange = useCallback(
		(interactionType, index) => {
			setSelectedIndex(index);
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'viewed',
					actionSubject: 'item',
				}),
				'aiUpsellInEditorFreeCarouselItem',
				{
					interactionType,
					carouselItemIndex: index,
				},
			);
		},
		[createAnalyticsEvent],
	);

	// TODO Why the first item is not fired
	const onFocusItem: UpsellModalProps['carousel']['onFocusItem'] = (interactionType, index) => {
		setSelectedIndex(index);
		onCarouselItemChange(interactionType, index);
	};

	// Modal Listeners
	const onClose: UpsellModalProps['close']['onClose'] = useCallback(
		(trigger) => {
			onModalClose(trigger);

			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'closed',
					actionSubject: 'modal',
				}),
				'aiUpsellInEditorFreeModal',
				{ closeMethod: trigger },
			);
		},
		[createAnalyticsEvent, onModalClose],
	);

	const onCtaClick = useCallback(() => {
		onClose('close');
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'aiUpsellInEditorFreeModalCta',
		);
	}, [createAnalyticsEvent, onClose]);

	// -----  UI Components -----
	const items = useMemo(
		() => [
			formatMessage(messages.writeCleanerDescriptions),
			formatMessage(messages.writeInProfessionalTone),
			formatMessage(messages.brainstorm),
			formatMessage(messages.fixSpellingAndGrammar),
		],
		[formatMessage],
	);

	const carouselItems = useMemo(
		() =>
			[
				<GifBackground key="item0">
					<Image
						src={improveYourWriting}
						data-testid={`${testId}-carousel-cleaner-descriptions`}
						alt={`${items[0]}`}
					/>
				</GifBackground>,
				<GifBackground key="item1">
					<Image
						src={changeYourTone}
						data-testid={`${testId}-carousel-professional-tone`}
						alt={`${items[1]}`}
					/>
				</GifBackground>,
				<GifBackground key="item2">
					<Image
						src={brainstorm}
						data-testid={`${testId}-carousel-acceptance-criteria`}
						alt={`${items[2]}`}
					/>
				</GifBackground>,
				<GifBackground key="item3">
					<Image
						src={fixSpellingAndGrammar}
						data-testid={`${testId}-carousel-fix-spelling`}
						alt={`${items[3]}`}
					/>
				</GifBackground>,
			].map((component) => ({
				caption: '',
				component: {
					light: component,
					dark: component,
				},
			})),
		[items, testId],
	);

	const cta = useMemo(
		() => ({
			url: startTrialUrl,
			// If there's an error fetching the URL, we will keep the button on loading state and show a flag
			isLoading: startTrialUrl === FALLBACK_URL,
			message: formatMessage(messages.tryItFree),
			onClick: onCtaClick,
		}),
		[formatMessage, onCtaClick, startTrialUrl],
	);

	// -----  Error handling -----
	const onQueryError = useCallback(
		(error: Error, attributes: AnalyticsAttributes) => {
			showFlag({
				id: 'aiUpsellInEditorFree-GetStartPremiumTrialUrlQueryError',
				type: 'error',
				title: messages.queryError,
			});

			fireErrorAnalytics({
				meta: {
					id: 'aiUpsellInEditorFree-GetStartPremiumTrialUrlQuery',
					packageName: 'jiraGrowthAiUpsellInEditorFreeModal',
					teamName: 'flywheel-tako',
				},
				error,
				attributes,
			});
		},
		[showFlag],
	);

	// -----  Data -----
	// The best will be to use PreloadedQuery, but the EntryPoint was causing problems with the editor focus (we are not sure why)
	// I could use useLazyLoadQuery to fetch the data, but it works with Suspense and fallbacks the entire modal instead of only the Button
	// We will use fetchQuery for now, but we can refactor to pass the ButtonComponent as a prop instead
	useEffect(() => {
		fetchQuery<uiAiUpsellInEditorFreeModal_GetStartPremiumTrialUrlQuery>(
			environment,
			graphql`
				query uiAiUpsellInEditorFreeModal_GetStartPremiumTrialUrlQuery(
					$cloudId: ID!
					$productKey: String!
					$offeringKey: ID!
				) {
					tenantContexts(cloudIds: [$cloudId], hostNames: []) {
						entitlementInfo(hamsProductKey: $productKey) {
							entitlement {
								experienceCapabilities {
									changeOffering(offeringKey: $offeringKey) {
										isAvailableToUser
										experienceUrl
									}
								}
							}
						}
					}
				}
			`,
			{
				cloudId,
				productKey: getHamsProductKeyForProject(projectType),
				offeringKey: getProductOfferingKeyForProject(projectType, PREMIUM_EDITION),
			},
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (response: uiAiUpsellInEditorFreeModal_GetStartPremiumTrialUrlQuery$data) => {
				if (response?.tenantContexts) {
					const changeOffering =
						response.tenantContexts[0]?.entitlementInfo?.entitlement?.experienceCapabilities
							?.changeOffering;
					const experienceUrl = changeOffering?.experienceUrl;
					if (experienceUrl) {
						const origin = new OriginTracer({ product: 'jira' });
						setStartTrialUrl(origin.addToUrl(experienceUrl));
					} else {
						onQueryError(new Error('Failed to fetch URL'), {
							isAvailableToUser: Boolean(changeOffering?.isAvailableToUser),
							projectType,
						});
					}
				}
			},
			error: (error: Error) => {
				onQueryError(error, {});
			},
		});
	}, [cloudId, environment, onQueryError, projectType]);

	return (
		<ModalTransition>
			<Box testId={testId}>
				<UpsellModal
					// Same size as the Create Issue modal
					width={800}
					testId="growth-ai-upsell-in-editor-free-modal.ui.ai-upsell-modal"
					header={formatMessage(messages.header)}
					headerLogo={<Logo projectType={projectType} />}
					carousel={{
						items: carouselItems,
						selectedItem: hasSelected ? selectedIndex : undefined,
						onFocusItem,
						slideInterval: 10,
					}}
					cta={cta}
					close={{
						message: formatMessage(messages.back),
						onClose,
					}}
				>
					<ModalContent
						items={items}
						selectedIndex={selectedIndex}
						onItemClick={(index: number) => {
							setSelectedIndex(index);
							setHasSelected(true);
							onCarouselItemChange('hover', index);
						}}
						baseTestId={testId}
					/>
				</UpsellModal>
				<FireScreenAnalytics />
			</Box>
		</ModalTransition>
	);
}

function ModalContent(props: ModalContentProps) {
	const { items, selectedIndex, onItemClick, baseTestId } = props;

	const { formatMessage } = useIntl();

	return (
		<Stack space="space.400" xcss={containerStyles}>
			<Text>
				{formatMessage(messages.description, {
					a: (msg: ReactNode) => (
						<Link target="_blank" href="https://www.atlassian.com/software/jira/premium">
							{msg}
						</Link>
					),
				})}
			</Text>

			<Stack space="space.300" role="list">
				{items.map((item, index) => (
					<LineItem
						testId={`${baseTestId}-line-item-${index + 1}`}
						key={item}
						lineItem={item}
						isSelected={selectedIndex === index}
						onInteraction={() => onItemClick(index)}
					/>
				))}
			</Stack>
		</Stack>
	);
}

const containerStyles = xcss({
	marginTop: 'space.300',
	marginBottom: 'space.1000',
});

const backgroundStyles = xcss({
	width: '300px',
	backgroundRepeat: 'no-repeat',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: '270px',
});
